import { BlueprintVertex } from '../vertex.js';
import * as constants from '../constants.js';

export class BlueprintImage extends BlueprintVertex {
  /** @property {Promise|null} loaded - A promise that resolves when the image has loaded  */
  loaded = null;

  /**
   * @param {fabric.Canvas} canvas - the fabric canvas
   * @param {Object} config - vertex configuration
   * @param {string} config.src - The URL for the image to load when a fabric image is not passed
   * @param {fabric.Image} [config.fabricObject] - An existing fabric image object, ignores config.src
   * @param {Object=} config.fabricOptions - Image options to be passed into fabric
   * @returns {BlueprintImage}
   */
  constructor(canvas, { src, fabricObject, fabricOptions = {} }) {
    super(canvas, { vertexType: constants.CELL_TYPES.image });

    // already a fabric object
    if (fabricObject) {
      this.ref = fabricObject;
      this.loaded = Promise.resolve();
    } else {
      const image = new Image();
      let resolveLoaded;

      image.crossOrigin = 'anonymous';
      image.src = src;

      this.loaded = new Promise(res => {
        resolveLoaded = res;
      });

      image.onload = () => {
        const { top, left } = this.canvas.getCenter();

        this.ref = new fabric.Image(image);

        this.ref.set({
          angle: 0,
          snapAngle: 22.5,
          ...fabricOptions,
        });

        if (!fabricOptions.scaleY || !fabricOptions.scaleX) {
          this.ref.scaleToWidth(300);
        }

        /** @todo we should conditionally avoid this if `position` is set in `options` */
        this.ref.setPositionByOrigin({
          x: left - 150,
          y: top - 150,
        });

        resolveLoaded();
      };
    }
  }

  /**
   * @property {string} src - The image source
   * @returns {string} The current image source
   */
  get src() {
    return this.ref.getSrc();
  }

  get clonableProps() {
    const { angle, width, height, scaleX, scaleY } = this.ref;

    return {
      angle,
      width,
      height,
      scaleX,
      scaleY,
    };
  }

  /** Clone this image by returning a new instance */
  clone() {
    return new BlueprintImage(this.canvas, {
      src: this.src,
      fabricOptions: this.clonableProps,
    });
  }
}
